import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, ShieldCheck, ChartPie, Landmark, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Strategic Advisory",
  description: "Operator-led guidance for founders navigating the transition from early product development to market entry with clinical precision.",
  stat: "Selective",
  icon: ChartPie
}, {
  title: "Growth Architecture",
  description: "We build the systems and frameworks necessary to scale without the typical startup chaos, focusing on sustainable customer acquisition.",
  stat: "Sustainable",
  icon: ShieldCheck
}, {
  title: "Ops & Execution",
  description: "Deep operational support to bridge the gap between vision and reality through rigorous process design and resource management.",
  stat: "Operator-Led",
  icon: Landmark
}];
export default function CapitalControlExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="51148" id="areas-of-expertise" className="relative w-full bg-background overflow-hidden dark" ref={ref}>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[800px]">
        {}
        <div className="flex flex-col justify-center px-6 py-16 lg:px-20 lg:py-24 xl:px-32 order-2 lg:order-1">
          <div className={`transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="mb-8">
              <Badge variant="outline" className="mb-4 border-primary/20 text-primary px-3 py-1 rounded-full uppercase tracking-widest text-[10px] font-default font-semibold">
                Our Focus
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-medium tracking-tight text-foreground mb-6">
                Strategic Advisory & <br className="hidden md:block" />
                Operator-Led Growth
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl font-default">
                1066 Ventures provides the structural support required for early-stage founders to move from validation to scale. We act as operators, ensuring every venture is built on a foundation of calm confidence.
              </p>
            </div>

            <Separator className="my-10 bg-border/40" />

            <div className="space-y-10">
              {services.map((service, i) => <div key={i} data-index={i} className={`group relative pl-4 transition-all duration-700 delay-[${100 + i * 100}ms] ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}>
                  {}
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-border/20 group-hover:bg-primary transition-colors duration-300" />
                  
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <service.icon className="w-5 h-5 text-primary/70" />
                      <h3 className="text-xl font-default font-medium text-foreground">
                        {service.title}
                      </h3>
                    </div>
                    <Badge variant="secondary" className="w-fit bg-secondary/30 text-secondary-foreground border-0 font-default font-medium">
                      {service.stat}
                    </Badge>
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed mb-6 max-w-lg font-default">
                    {service.description}
                  </p>
                  
                  {i < services.length - 1 && <Separator className="bg-border/20 mt-8" />}
                </div>)}
            </div>

            <div className="mt-12 pt-4">
              <Button asChild size="lg" className="h-12 px-8 text-base bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-default">
                <Link to="#">
                  Tell Us What You’re Building
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="relative h-[400px] lg:h-auto w-full order-1 lg:order-2 bg-muted">
          <div className="absolute inset-0 bg-primary/5 mix-blend-multiply z-10" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1622697/img/infrastructure_real_estate_development.png" alt="Abstract architectural lines representing venture structure" className="absolute inset-0 w-full h-full object-cover grayscale opacity-80" />
          
          {}
          <div className={`absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:w-80 bg-card/95 backdrop-blur-md p-6 border border-border/30 shadow-2xl z-20 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-primary/10 rounded-full shrink-0">
                <CircleCheck className="w-6 h-6 text-primary" />
              </div>
              <div className="w-full">
                <p className="text-xs font-default font-semibold text-muted-foreground uppercase tracking-widest mb-1">
                  Venture Status
                </p>
                <p className="text-lg font-default font-medium text-foreground">
                  Validated & Scaling
                </p>
                <div className="mt-3 h-0.5 w-full bg-secondary/30 rounded-full overflow-hidden">
                  <div className="h-full w-[92%] bg-primary rounded-full" />
                </div>
                <p className="text-[10px] text-right text-muted-foreground mt-1 font-default">92% Ready</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}